import React, { useState, useEffect, useRef } from 'react';
import {
  MessageSquare,
  Clock,
  Pause,
  Play,
  RotateCcw,
  CheckCircle,
  Sparkles
} from 'lucide-react';
import FeatureStatusBadge from '../shared/FeatureStatusBadge';
import SectionControls from '../shared/SectionControls';

const FreewritingDemo = ({ data, onAnimationComplete, onContinue, isLastSection = false }) => {
  const [isInView, setIsInView] = useState(false);
  const [typewriterText, setTypewriterText] = useState('');
  const [isWriting, setIsWriting] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [showEncouragement, setShowEncouragement] = useState(false);
  const [animationsComplete, setAnimationsComplete] = useState(false);
  const [showCompletionBanner, setShowCompletionBanner] = useState(false);
  const sectionRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
          // Auto-start the writing demo
          setTimeout(() => {
            setIsWriting(true);
          }, 1000);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isInView]);

  // Typewriter effect with pause - three part animation
  useEffect(() => {
    if (isWriting && isInView) {
      // Define the two parts explicitly
      const firstHalf = "The bus route to my school makes absolutely no sense. Today it took 45 minutes to go 3 miles because it zigzags through neighborhoods in this totally random pattern. I could cut out half the stops, change the order, and save everyone 10 minutes... ";

      const secondHalf = "but I already know they'll be just like That's how we've always done it or whatever. It's sort of like how my dad still uses the same route to work every day even though Waze keeps telling him there's a faster way. Same energy. Why do people get so attached to broken things when there are much better ways right there in front of them?";

      // Alternative: Auto-calculate split point (uncomment if needed)
      // const fullText = firstHalf + secondHalf;
      // const halfwayChar = Math.floor(fullText.length / 2);
      // let splitPoint = halfwayChar;
      // while (splitPoint < fullText.length && fullText[splitPoint] !== ' ') {
      //   splitPoint++;
      // }
      // const firstHalf = fullText.substring(0, splitPoint);
      // const secondHalf = fullText.substring(splitPoint);

      // Part 1: Type first half
      let index1 = 0;
      const timer1 = setInterval(() => {
        if (index1 <= firstHalf.length) {
          setTypewriterText(firstHalf.substring(0, index1));
          index1++;
        } else {
          clearInterval(timer1);

          // Part 2: Show encouragement message
          setShowEncouragement(true);

          // Part 3: After pause, continue with second half
          setTimeout(() => {
            // Start fading out encouragement when writing resumes
            setTimeout(() => setShowEncouragement(false), 100); // Small delay to trigger fade

            let index2 = 0;
            const timer2 = setInterval(() => {
              if (index2 <= secondHalf.length) {
                setTypewriterText(firstHalf + secondHalf.substring(0, index2));
                index2++;
              } else {
                clearInterval(timer2);
                // Mark animation as complete
                setIsWriting(false);
                setAnimationsComplete(true);
                setShowCompletionBanner(true);
                onAnimationComplete && onAnimationComplete();
              }
            }, 10);
          }, 2000); // 2 second pause
        }
      }, 10);

      return () => {
        clearInterval(timer1);
      };
    }
  }, [isWriting, isInView]);

  // Timer effect
  useEffect(() => {
    if (isWriting) {
      timerRef.current = setInterval(() => {
        setTimeElapsed(prev => prev + 1);
      }, 500);
    } else {
      clearInterval(timerRef.current);
    }

    return () => clearInterval(timerRef.current);
  }, [isWriting]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div ref={sectionRef} className="max-w-6xl mx-auto px-4">
      {/* Section Header */}
      <div className="text-center mb-12">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-50 rounded-full">
            <MessageSquare className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700">
              Freewriting Engine
            </span>
          </div>
        </div>
        <h2 className="text-4xl font-bold text-slate-900 mb-4">
          Capture Your Authentic Voice
        </h2>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
        Write messy. Write honest. Write badly. You have a judgment-free space to dump your thoughts before anyone tells you how to "improve" them.
        </p>
      </div>

      {/* Fixed-width grid to prevent layout shift */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 w-full">
        {/* Main Freewriting Interface */}
        <div className="lg:col-span-2 w-full">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 w-full">
            {/* Header with Timer */}
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-600" />
                    <span className="font-mono text-lg font-medium text-slate-900">
                      {formatTime(timeElapsed)}
                    </span>
                  </div>
                  <div className="text-sm text-slate-600">
                    {Math.floor(typewriterText.length / 5)} words
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsWriting(!isWriting)}
                    className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                  >
                    {isWriting ? (
                      <Pause className="w-4 h-4 text-slate-600" />
                    ) : (
                      <Play className="w-4 h-4 text-slate-600" />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setTypewriterText('');
                      setTimeElapsed(0);
                      setIsWriting(false);
                      setShowEncouragement(false);
                      setAnimationsComplete(false);
                      setShowCompletionBanner(false);
                    }}
                    className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                  >
                    <RotateCcw className="w-4 h-4 text-slate-600" />
                  </button>
                </div>
              </div>
            </div>

            {/* Writing Area */}
            <div className="p-6 relative">
              <div className="min-h-[400px] text-slate-800 leading-relaxed">
                {typewriterText && (
                  <>
                    {typewriterText}
                    {isWriting && <span className="animate-pulse border-r-2 border-azure-500 ml-1"></span>}
                  </>
                )}

                {/* Encouragement Message */}
                <div className={`absolute bottom-2 left-6 right-6 text-center transition-opacity duration-[1500ms] ${
                  showEncouragement ? 'opacity-100' : 'opacity-0'
                }`}>
                  <span className="text-sm text-emerald-600 font-medium bg-emerald-50 px-3 py-1 rounded-full inline-flex items-center gap-2">
                    <span className="animate-pulse">✨</span>
                    You're in the flow - keep going
                    <span className="animate-pulse">✨</span>
                  </span>
                </div>

                {!typewriterText && !isWriting && (
                  <div className="text-slate-400 italic">
                    Start writing... your thoughts will flow here without interruption.
                  </div>
                )}
              </div>

              {/* Completion Banner */}
              {showCompletionBanner && (
                <div className={`absolute bottom-6 left-6 right-6 bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-lg p-4 transition-all duration-500 ${
                  showCompletionBanner ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-emerald-900">Freewriting Session Complete!</div>
                      <div className="text-sm text-emerald-700 mt-1">
                        {Math.floor(typewriterText.length / 5)} words captured • {formatTime(timeElapsed)} of uninterrupted flow
                      </div>
                    </div>
                    <Sparkles className="w-5 h-5 text-emerald-500 animate-pulse" />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Sidebar - Freewriting Benefits */}
        <div className="space-y-6 w-full">
          {/* Session Stats */}
          <div className={`bg-white rounded-xl shadow-sm border-2 transition-all duration-500 p-6 ${
            animationsComplete ? 'border-emerald-400' : 'border-slate-200'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">Session Progress</h3>
              {animationsComplete && (
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Time Writing</span>
                <span className="font-mono text-sm font-medium">{formatTime(timeElapsed)}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Words Generated</span>
                <span className="font-mono text-sm font-medium">{Math.floor(typewriterText.length / 5)}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Flow State</span>
                <span className={`text-sm font-medium ${
                  animationsComplete ? 'text-emerald-600' : isWriting ? 'text-emerald-600' : 'text-slate-600'
                }`}>
                  {animationsComplete ? 'Complete ✓' : isWriting ? 'Active' : 'Paused'}
                </span>
              </div>
            </div>

            {animationsComplete && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="text-sm font-medium text-emerald-700 text-center animate-fadeIn">
                  Great freewriting session!
                </div>
              </div>
            )}
          </div>

          {/* Freewriting Principles */}
          <div className="bg-gradient-to-br from-emerald-50 to-azure-50 rounded-xl border border-emerald-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Freewriting Rules</h3>

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-emerald-600 text-xs">1</span>
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-900">Keep writing</div>
                  <div className="text-xs text-slate-600">Don't stop, even if you run out of ideas</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-emerald-600 text-xs">2</span>
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-900">No editing</div>
                  <div className="text-xs text-slate-600">Grammar and spelling don't matter yet</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-emerald-600 text-xs">3</span>
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-900">Follow thoughts</div>
                  <div className="text-xs text-slate-600">Let your mind wander and explore</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-emerald-600 text-xs">4</span>
                </div>
                <div>
                  <div className="text-sm font-medium text-slate-900">Stay honest</div>
                  <div className="text-xs text-slate-600">Write what you really think and feel</div>
                </div>
              </div>
            </div>
          </div>


        </div>
      </div>

      {/* Section Controls */}
      <SectionControls
        onReplay={() => {
          setTypewriterText('');
          setTimeElapsed(0);
          setIsWriting(true);
          setShowEncouragement(false);
          setAnimationsComplete(false);
          setShowCompletionBanner(false);
        }}
        onContinue={onContinue}
        isLastSection={isLastSection}
        showControls={animationsComplete}
      />

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default FreewritingDemo;